import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { QStep, QStepperProgress, QSteps } from '@qualio/ui-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import ReassignEntities from './ReassignEntities';
import Summary from './Summary';
import { CommonProps, AllCategories } from '../types';
import { StepStatusOptions } from './types';
import { isEmpty } from '../../../utils/object';
import { EntitiesState, EntityDispatch } from '../state';

type StepsProps = {
  entityState: EntitiesState;
  dispatch: EntityDispatch;
} & Pick<CommonProps, 'users' | 'setNextButtonEnabled' | 'setShowNextButton' | 'incrementStep'>;

type StepProgressMapType = Record<AllCategories, StepStatusOptions>;

/**
 * This component is what handles rendering the individual steps as well as
 * storing the "status" of each step, i.e. "completed", "invalid", etc
 */
const Steps = ({
  users,
  setNextButtonEnabled,
  setShowNextButton,
  incrementStep,
  entityState,
  dispatch,
}: StepsProps): ReactElement => {
  const { t } = useTranslation();
  const { changeManagementDocTemplate, eventCollaborators } = useFlags();
  const issuesStepTitle = t(eventCollaborators ? 'reassignEntities.steps.events' : 'reassignEntities.steps.issues');

  const qualityUsers = useMemo(() => {
    return users.filter((user) => user.role === 'quality');
  }, [users]);

  const [stepStatus, setStepStatus] = useState<StepProgressMapType>({
    owner: 'default',
    changeRequestOwner: 'default',
    documentTemplateOwner: 'default',
    approver: 'default',
    reviewer: 'default',
    issues: 'default',
    tasks: 'default',
    default_owner: 'default',
    eventTemplateOwner: 'default',
    control: 'default',
  });

  const setStatusForStep = (stepName: keyof StepProgressMapType, status: StepStatusOptions) => {
    setStepStatus((currentStepStatus: StepProgressMapType) => {
      return {
        ...currentStepStatus,
        [stepName]: status,
      };
    });
  };
  const setOwnerStatus = useCallback(setStatusForStep.bind(null, 'owner'), []);
  const setChangeRequestOwnerStatus = useCallback(setStatusForStep.bind(null, 'changeRequestOwner'), []);
  const setDocumentTemplateOwnerStatus = useCallback(setStatusForStep.bind(null, 'documentTemplateOwner'), []);
  const setApproverStatus = useCallback(setStatusForStep.bind(null, 'approver'), []);
  const setReviewerStatus = useCallback(setStatusForStep.bind(null, 'reviewer'), []);
  const setIssuesStatus = useCallback(setStatusForStep.bind(null, 'issues'), []);
  const setTasksStatus = useCallback(setStatusForStep.bind(null, 'tasks'), []);
  const setDefaultOwnerStatus = useCallback(setStatusForStep.bind(null, 'default_owner'), []);
  const setEventTemplateOwnerStatus = useCallback(setStatusForStep.bind(null, 'eventTemplateOwner'), []);
  const setControlStatus = useCallback(setStatusForStep.bind(null, 'control'), []);

  const {
    owner: ownerItems,
    changeRequestOwner: changeRequestOwnerItems,
    documentTemplateOwner: documentTemplateOwnerItems,
    approver: approverItems,
    reviewer: reviewerItems,
    issues: issueItems,
    tasks: taskItems,
    default_owner: defaultOwnerItems,
    eventTemplateOwner: eventTemplateOwnerItems,
    controls: controlItems,
  } = entityState;

  return (
    <QSteps>
      <QStepperProgress />
      {!isEmpty(ownerItems) && (
        <QStep variant={stepStatus.owner} title={t('reassignEntities.steps.owner')}>
          <ReassignEntities
            key="ownerStep"
            category="owner"
            title={t('reassignEntities.steps.owner')}
            setNextButtonEnabled={setNextButtonEnabled}
            incrementStep={incrementStep}
            items={ownerItems}
            dispatch={dispatch}
            users={users}
            updateStepStatus={setOwnerStatus}
          />
        </QStep>
      )}
      {changeManagementDocTemplate && !isEmpty(documentTemplateOwnerItems) && (
        <QStep variant={stepStatus.documentTemplateOwner} title={t('reassignEntities.steps.documentTemplateOwner')}>
          <ReassignEntities
            key="documentTemplateOwnerStep"
            category="documentTemplateOwner"
            title={t('reassignEntities.steps.documentTemplateOwner')}
            setNextButtonEnabled={setNextButtonEnabled}
            incrementStep={incrementStep}
            items={documentTemplateOwnerItems}
            dispatch={dispatch}
            users={qualityUsers}
            updateStepStatus={setDocumentTemplateOwnerStatus}
          />
        </QStep>
      )}
      {!isEmpty(changeRequestOwnerItems) && (
        <QStep variant={stepStatus.changeRequestOwner} title={t('reassignEntities.steps.changeRequestOwner')}>
          <ReassignEntities
            key="changeRequestOwnerStep"
            category="changeRequestOwner"
            title={t('reassignEntities.steps.changeRequestOwner')}
            setNextButtonEnabled={setNextButtonEnabled}
            incrementStep={incrementStep}
            items={changeRequestOwnerItems}
            dispatch={dispatch}
            users={users}
            updateStepStatus={setChangeRequestOwnerStatus}
          />
        </QStep>
      )}
      {!isEmpty(eventTemplateOwnerItems) && (
        <QStep variant={stepStatus.eventTemplateOwner} title={t('reassignEntities.steps.eventTemplateOwner')}>
          <ReassignEntities
            key="eventTemplateOwnerStep"
            category="eventTemplateOwner"
            title={t('reassignEntities.steps.eventTemplateOwner')}
            setNextButtonEnabled={setNextButtonEnabled}
            incrementStep={incrementStep}
            items={eventTemplateOwnerItems}
            dispatch={dispatch}
            users={users}
            updateStepStatus={setEventTemplateOwnerStatus}
          />
        </QStep>
      )}
      {!isEmpty(approverItems) && (
        <QStep variant={stepStatus.approver} title={t('reassignEntities.steps.approver')}>
          <ReassignEntities
            key="approverStep"
            category="approver"
            title={t('reassignEntities.steps.approver')}
            setNextButtonEnabled={setNextButtonEnabled}
            incrementStep={incrementStep}
            items={approverItems}
            dispatch={dispatch}
            users={users}
            updateStepStatus={setApproverStatus}
          />
        </QStep>
      )}
      {!isEmpty(reviewerItems) && (
        <QStep variant={stepStatus.reviewer} title={t('reassignEntities.steps.reviewer')}>
          <ReassignEntities
            key="reviewerStep"
            category="reviewer"
            title={t('reassignEntities.steps.reviewer')}
            setNextButtonEnabled={setNextButtonEnabled}
            incrementStep={incrementStep}
            items={reviewerItems}
            dispatch={dispatch}
            users={users}
            updateStepStatus={setReviewerStatus}
          />
        </QStep>
      )}
      {!isEmpty(issueItems) && (
        <QStep variant={stepStatus.issues} title={issuesStepTitle}>
          <ReassignEntities
            key="issuesStep"
            category="issues"
            title={issuesStepTitle}
            setNextButtonEnabled={setNextButtonEnabled}
            incrementStep={incrementStep}
            items={issueItems}
            dispatch={dispatch}
            users={users}
            updateStepStatus={setIssuesStatus}
          />
        </QStep>
      )}
      {!isEmpty(taskItems) && (
        <QStep variant={stepStatus.tasks} title={t('reassignEntities.steps.tasks')}>
          <ReassignEntities
            key="tasksStep"
            category="tasks"
            title={t('reassignEntities.steps.tasks')}
            setNextButtonEnabled={setNextButtonEnabled}
            incrementStep={incrementStep}
            items={taskItems}
            dispatch={dispatch}
            users={users}
            updateStepStatus={setTasksStatus}
          />
        </QStep>
      )}
      {!isEmpty(defaultOwnerItems) && (
        <QStep variant={stepStatus.default_owner} title={t('reassignEntities.steps.default_owner')}>
          <ReassignEntities
            key="defaultOwnerStep"
            category="default_owner"
            title={t('reassignEntities.steps.default_owner')}
            setNextButtonEnabled={setNextButtonEnabled}
            incrementStep={incrementStep}
            items={defaultOwnerItems}
            dispatch={dispatch}
            users={users}
            updateStepStatus={setDefaultOwnerStatus}
          />
        </QStep>
      )}
      {!isEmpty(controlItems) && (
        <QStep variant={stepStatus.control} title={t('reassignEntities.steps.control')}>
          <ReassignEntities
            key="controlsStep"
            category="control"
            title={t('reassignEntities.steps.control')}
            setNextButtonEnabled={setNextButtonEnabled}
            incrementStep={incrementStep}
            items={controlItems}
            dispatch={dispatch}
            users={users}
            updateStepStatus={setControlStatus}
          />
        </QStep>
      )}
      <QStep title={t('reassignEntities.steps.summary')}>
        <Summary
          key="summaryStep"
          title={t('reassignEntities.steps.summary')}
          setShowNextButton={setShowNextButton}
          entityState={entityState}
        />
      </QStep>
    </QSteps>
  );
};

export default Steps;
